<script setup>
import {computed, ref, watch} from "vue";
import {useForm, usePage} from "@inertiajs/inertia-vue3";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import '@vuepic/vue-datepicker/dist/main.css'
import Datepicker from "@vuepic/vue-datepicker";
import dayjs from "dayjs";
import TextInput from "@/Components/TextInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import Checkbox from "@/Components/Checkbox.vue";
import {Inertia} from "@inertiajs/inertia";
import {useHelpers, useTenantTextRules} from "@/helpers";
import NumberInput from "../../NumberInput.vue";
import TextareaInput from "../../TextareaInput.vue";
import SelectInput from "../../SelectInput.vue";

const currentUser = usePage().props.value.auth.user;
const helpers = useHelpers();
const { resolveTextRule } = useTenantTextRules();
const props = defineProps({
  carriers: Array,
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  }
});
const mapShipping = (shipping) => {
  return {
    shipping_id: shipping.shipping_id,
    carrier_id: shipping.carrier_id,
    deliv_by: shipping.deliv_by,
    deliv_date: (shipping.deliv_date) ? shipping.deliv_date * 1000 : null,
    ship_from: shipping.ship_from,
    ship_to: shipping.ship_to,
    shipping_fee: shipping.shipping_fee || 0.0,
    shiponclient: shipping.shiponclient,
    shippedon: shipping.shippedon,
    shipping_notes: shipping.shipping_notes,
    quantities: shipping.quantities.map(quantity => {
      quantity.is_received = quantity.received_at !== null;
      return quantity;
    }),
    tracking_numbers: shipping.tracking_numbers,
  };
}

const shippingDirectionOptions = ['Vendor', 'Client', 'Office'];
const projectProperty = computed(() => usePage().props.value.project);
let project = projectProperty.value;
watch(projectProperty, (value) => {
  project = projectProperty.value;
  if (Object.keys(usePage().props.value.errors).length > 0) {
    return;
  }
  shippingForm.project_order.order_placeon = orderPlaced.value = project.order_placeon;
  shippingForm.project_order.order_eta_on = orderEta.value = project.order_eta_on;
  shippingForm.project_order.bid_number = bidNumber.value = project.bid_number;
  shippingForm.shipping = project.shipping.map(shipping => mapShipping(shipping));
});

const minDate = dayjs().toDate();
const orderPlaced = ref(project.order_placeon || null);
const orderEta = ref(project.order_eta_on || null);
const bidNumber = ref(project.bid_number || null);
const shippingForm = useForm({
  _method: 'PUT',
  shipping: project.shipping.map(shipping => mapShipping(shipping)),
  project_order: {
    bid_number: project.bid_number,
    order_placeon: project.order_placeon,
    order_eta_on: project.order_eta_on
  },
});

const savingShipping = ref(false);
const saveShippingForm = () => {
  if (orderPlaced) {
    shippingForm.project_order.order_placeon = orderPlaced;
    shippingForm.project_order.order_eta_on = orderEta;
    shippingForm.project_order.bid_number = bidNumber;
  }
  savingShipping.value = true;
  shippingForm.shipping = shippingForm.shipping.map(shipping => {
    return {...shipping, ...{deliv_date: (shipping.deliv_date && String(shipping.deliv_date).length > 11) ? shipping.deliv_date / 1000 : shipping.deliv_date}};
  })
  shippingForm.post(route('admin.projects.update', {project: project.pid}), {
    preserveScroll: true,
    onFinish() {
      savingShipping.value = false;
    },
    onError() {
      shippingForm.shipping = shippingForm.shipping.map(shipping => {
        return {...shipping, ...{deliv_date: (shipping.deliv_date && String(shipping.deliv_date).length <= 11) ? shipping.deliv_date * 1000 : shipping.deliv_date}};
      })
    }
  });
  savingShipping.value = false;
}

const oldShippingSectionsCount = ref(shippingForm.shipping.length);
const shippingSections = ref(null);
const addNewShippingRow = () => {
  shippingForm.shipping.push({
    shipping_id: null,
    carrier_id: null, // Add select for this one, and disable it once created
    shippedon: null,
    deliv_date: null,
    deliv_by: null,
    ship_from: 'Vendor',
    ship_to: 'Vendor',
    shipping_notes: null,
    shiponclient: false,
    tracking_numbers: [],
    quantities: project.styles.map(style => {
      return {
        style: {
          style: style.style,
          vendor_style: style.vendor_style,
          garments: style.garments,
          prj_style_id: style.prj_style_id,
          quantity_shipped: style.quantity_shipped
        },
        prj_style_id: style.prj_style_id,
        qty_id: null,
        is_received: false,
        qty_ship: 0
      }
    })
  });
}

watch(() => shippingSections, () => {
  if (shippingSections.value.length > oldShippingSectionsCount.value) {
    shippingSections.value[shippingSections.value.length - 1].scrollIntoView();
  }
  oldShippingSectionsCount.value = shippingSections.value.length;
}, {deep: true});

const carrierWebLink = (carrier_id, trackingNumber) => {
  const carrier = _.find(props.carriers, (carrier) => carrier.carrier_id === carrier_id);

  return `${carrier.weblink}${trackingNumber}`;
}

const isHandDelivered = (shipping) => {
  return parseInt(shipping.carrier_id) === 6;
}

const isShippedOnDisabled = (shipping) => {
  if (helpers.userCanManageProject(currentUser)) {
    return false;
  }
  if (helpers.userIsVendor(currentUser)) {
    return shipping.shipping_id > 0;
  }
  return true
}

const addTrackingNumber = (shippingIndex) => {
  shippingForm.shipping[shippingIndex].tracking_numbers.push({
    track_id: null, tracking_no: null
  });
}

const deleteTrackingNumber = (shippingIndex, trackingIndex) => {
  const shipping = shippingForm.shipping[shippingIndex];
  const tracking = shipping.tracking_numbers[trackingIndex];
  if (tracking.track_id === null) {
    shippingForm.shipping[shippingIndex].tracking_numbers.splice(trackingIndex, 1);
  } else {
    Inertia.post(route('admin.projects.shipping.tracking-numbers.destroy', {
      projectid: project.pid,
      shippingid: shipping.shipping_id,
      tracking_numberid: tracking.track_id
    }), {_method: 'DELETE'}, {
      preserveScroll: true
    })
  }
}

const deleteShippingRow = (shippingIndex) => {
  const shipping = shippingForm.shipping[shippingIndex];
  if (shipping.shipping_id === null) {
    shippingForm.shipping.splice(shippingIndex, 1);
  } else {
    Inertia.post(route('admin.projects.shipping.destroy', {
      projectid: project.pid,
      shippingid: shipping.shipping_id,
    }), {_method: 'DELETE'}, {
      preserveScroll: true
    })
  }
}
const shippingStyleLabel = computed(() =>
  resolveTextRule('project.shipping.table.style', 'Style', 'project.shipping.table.style')
);
const shippingSampleLabel = computed(() =>
  resolveTextRule('project.shipping.sample_label', 'Sample', 'project.shipping.sample_label')
);
const shippingQuantityOptionsRaw = computed(() =>
  resolveTextRule(
    'project.pricing.table.quantity.options',
    '',
    'project.pricing.table.quantity.options'
  )
);
const shippingQuantityOptionsMap = computed(() => {
  const raw = `${shippingQuantityOptionsRaw.value || ''}`.trim();

  if (!raw) {
    return new Map();
  }

  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return new Map();
    }

    return new Map(
      parsed
        .map((option, index) => {
          if (typeof option === 'string') {
            return [String(index + 1), option];
          }

          const label = `${option?.label ?? ''}`.trim();
          const rawValue = option?.value;
          const numericValue = Number(rawValue);

          if (!label || Number.isNaN(numericValue)) {
            return null;
          }

          return [String(numericValue), label];
        })
        .filter(Boolean)
    );
  } catch (error) {
    return new Map();
  }
});
const formatShippingQuantity = (value) => {
  const normalized = `${value ?? ''}`.trim();

  if (!normalized) {
    return value;
  }

  return shippingQuantityOptionsMap.value.get(normalized) || value;
};
const shippingQuantityOrderedLabel = computed(() =>
  resolveTextRule(
    'project.shipping.table.quantity_ordered',
    'Quantity Ordered',
    'project.shipping.table.quantity_ordered'
  )
);
const shippingLeftToShipLabel = computed(() =>
  resolveTextRule(
    'project.shipping.table.left_to_ship',
    'Left to Ship',
    'project.shipping.table.left_to_ship'
  )
);

</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <table class="min-w-full w-full text-left text-sm font-light">
      <thead class="border-b font-medium dark:border-neutral-500">
      <tr>
        <th scope="col" class="px-4 py-2">{{ shippingStyleLabel }}</th>
        <th scope="col" class="px-4 py-2">Size</th>
        <th scope="col" class="px-4 py-2">{{ shippingQuantityOrderedLabel }}</th>
        <th scope="col" class="px-4 py-2">{{ shippingLeftToShipLabel }}</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="style in project.styles"
          class="border-b transition duration-300 ease-in-out hover:bg-neutral-200">
        <td class="px-4 py-2 break-words" v-text="style.style"></td>
        <td class="px-4 py-2 break-words" v-text="style.vendor_style"></td>
        <td class="px-4 py-2 break-words" v-text="formatShippingQuantity(style.garments)"></td>
        <td class="px-4 py-2 break-words">
          <span v-text="formatShippingQuantity(style.garments - (style.quantity_shipped || 0))"></span>
        </td>
      </tr>
      </tbody>
    </table>
    <p v-if="project.styles === 0">No records...</p>
  </div>

  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <div class="sm:grid grid-cols-6 gap-6">
      <!-- Order Placed On -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="order-placed-on" value="Order Placed On"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="orderPlaced" format="MMMM d, yyyy"
                    :min-date="minDate"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="shippingForm.errors['project_order.order_placedon']"/>
      </div>
      <!-- Order ETA -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="order-placed-on" value="Order ETA"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="orderEta" format="MMMM d, yyyy"
                    :min-date="minDate"
                    :disabled="(!helpers.userIsAdmin(currentUser) && !helpers.userIsProjectManager(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="shippingForm.errors['project_order.order_eta_on']"/>
      </div>
      <!-- Sample -->
      <div class="col-span-6 sm:col-span-4">
        <InputLabel for="sample" :value="shippingSampleLabel"/>
        <Datepicker class="mt-1 block w-full disabled:bg-gray-50" v-model="bidNumber" format="MMMM d, yyyy"
                    :min-date="minDate" id="samplek"
                    :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                    model-type="MM/dd/yyyy" auto-apply :close-on-auto-apply="true" :enable-time-picker="false"/>
        <InputError class="mt-2" :message="shippingForm.errors['project_order.bid_number']"/>
      </div>
    </div>
  </div>

  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5"
       v-for="(shipping, index) in shippingForm.shipping" :id="`shipping-section-${index}`" ref="shippingSections">
    <p class="mb-2">
      <b>Tracking information <span v-text="(index + 1)"></span></b>
      <PrimaryButton type="button" class="ml-2" @click="deleteShippingRow(index)"
                     v-if="(helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))">Delete
        row
      </PrimaryButton>
    </p>
    <div>
      <table class="min-w-full w-full text-left text-sm font-light mb-2">
        <thead class="border-b font-medium dark:border-neutral-500">
        <tr>
          <th scope="col" class="px-4 py-2 w-44">Carrier</th>
          <th scope="col" class="px-4 py-2 w-72" v-if="!isHandDelivered(shipping)">Tracking Numbers</th>
          <th scope="col" class="px-4 py-2" v-if="!isHandDelivered(shipping)">Shipped On</th>
          <th scope="col" class="px-4 py-2 w-36">Shipped From</th>
          <th scope="col" class="px-4 py-2 w-36">Shipping To</th>
          <th scope="col" class="px-4 py-2 w-48">Shipping Fee</th>
          <th scope="col" class="px-4 py-2">On Clients<br>Carrier Account</th>
        </tr>
        </thead>
        <tbody>
          <tr class="border-b transition duration-300 ease-in-out hover:bg-neutral-200 text-sm">
          <!-- Carrier Selection Column - Fixed width -->
          <td class="px-3 py-1 break-words w-48">
            <SelectInput v-model="shippingForm.shipping[index].carrier_id"
              class="text-sm mt-1" padding="p-1.5">
              <option :value="null">Please select</option>
              <option v-for="carrier in carriers" :value="carrier.carrier_id" v-text="carrier.carrier_name"></option>
            </SelectInput>
            <InputError :message="shippingForm.errors[`shipping.${index}.carrier_id`]" class="text-xs"/>
            <div v-if="isHandDelivered(shipping)">
              <InputLabel for="delivery-date" value="Delivery Date" class="mt-1 text-xs"/>
              <Datepicker id="delivery-date" class="text-sm mt-1 block w-full disabled:bg-gray-50"
                :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                v-model="shippingForm.shipping[index].deliv_date"
                format="MMMM d, yyyy" :min-date="minDate" model-type="timestamp" auto-apply
                :close-on-auto-apply="true" :enable-time-picker="false"/>
              <InputError :message="shippingForm.errors[`shipping.${index}.deliv_date`]" class="text-xs"/>
              <InputLabel for="delivered-by" value="Delivered By" class="mt-1 text-xs"/>
              <TextInput
                id="delivered-by"
                type="text"
                class="text-sm mt-1 block w-full p-1.5"
                v-model="shippingForm.shipping[index].deliv_by"
                :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
              />
              <InputError :message="shippingForm.errors[`shipping.${index}.deliv_by`]" class="text-xs"/>
            </div>
          </td>
          
          <!-- Tracking Numbers Column - Medium width -->
          <td class="px-3 py-1 break-words w-64" v-if="!isHandDelivered(shipping)">
            <InputError :message="shippingForm.errors[`shipping.${index}.tracking_numbers`]" class="text-xs"/>
            <div class="grid grid-cols-5 items-center"
              v-for="(trackingNumber, trackingIndex) in shipping.tracking_numbers">
              <TextInput
                type="text"
                class="text-sm mt-1 block w-full col-span-4 p-1.5"
                v-model="shippingForm.shipping[index].tracking_numbers[trackingIndex].tracking_no"
                :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
              />
              <InputError :message="shippingForm.errors[`shipping.${index}.tracking_numbers.${trackingIndex}.tracking_no`]" class="text-xs"/>
              <div>
                <a class="fa fa-shipping-fast text-base ml-2 cursor-pointer mr-1" target="_blank"
                  :href="carrierWebLink(shipping.carrier_id, shippingForm.shipping[index].tracking_numbers[trackingIndex].tracking_no)"
                ></a>
                <span class="fa fa-trash text-base ml-2 cursor-pointer"
                  v-if="!hideSave && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))"
                  @click.prevent="deleteTrackingNumber(index, trackingIndex)"></span>
              </div>
            </div>
            <PrimaryButton type="button" class="mt-1 text-xs py-1 px-2" @click.prevent="addTrackingNumber(index)"
              v-if="(shippingForm.shipping[index].carrier_id !== null) && (helpers.userCanManageProject(currentUser) || helpers.userIsVendor(currentUser))">
              Add Tracking Number
            </PrimaryButton>
          </td>
          
          <!-- Shipped On Column - Fixed width for date picker -->
          <td class="px-3 py-1 break-words w-44" v-if="!isHandDelivered(shipping)">
            <Datepicker class="text-sm block w-full disabled:bg-gray-50" v-model="shippingForm.shipping[index].shippedon"
              :disabled="isShippedOnDisabled(shippingForm.shipping[index])"
              format="MMMM d, yyyy" :min-date="minDate" model-type="MM/dd/yyyy" auto-apply
              :close-on-auto-apply="true" :enable-time-picker="false"/>
            <InputError :message="shippingForm.errors[`shipping.${index}.shippedon`]" class="text-xs"/>
          </td>
          
          <!-- Ship From Column - Smaller fixed width -->
          <td class="px-3 py-1 break-words w-32">
            <SelectInput v-model="shippingForm.shipping[index].ship_from"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
              class="text-sm mt-1 disabled:bg-gray-50" padding="p-1.5">
              <option v-for="option in shippingDirectionOptions" :value="option" v-text="option"></option>
            </SelectInput>
          </td>
          
          <!-- Ship To Column - Smaller fixed width -->
          <td class="px-3 py-1 break-words w-32">
            <SelectInput v-model="shippingForm.shipping[index].ship_to"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
              class="text-sm mt-1 disabled:bg-gray-50" padding="p-1.5">
              <option v-for="option in shippingDirectionOptions" :value="option" v-text="option"></option>
            </SelectInput>
          </td>
          
          <!-- Shipping Fee Column - Small fixed width -->
          <td class="px-3 py-1 break-words w-22">
            <TextInput
              id="shipping-to-client"
              type="number"
              class="text-sm mt-1 block w-full p-1.5"
              v-model="shippingForm.shipping[index].shipping_fee"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
            />
          </td>
          
          <!-- Checkbox Column - Extra small fixed width -->
          <td class="px-3 py-1 break-words w-20">
            <InputLabel for="on-carrier-account" class="text-sm">
              <div class="flex items-center">
                <Checkbox id="on-carrier-account" class="ml-1"
                  :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
                  v-model:checked="shippingForm.shipping[index].shiponclient"/>
              </div>
            </InputLabel>
          </td>
        </tr>
        <tr class="border-b transition duration-300 ease-in-out hover:bg-neutral-200">
          <th scope="col" class="px-4 py-2">Notes</th>
          <td class="px-4 py-2 break-words" colspan="4">
            <TextareaInput
              v-model="shippingForm.shipping[index].shipping_notes"
              rows="4"
              id="notes"
              type="text"
              class="w-full block"
              autocomplete="notes"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
            />
            <InputError :message="shippingForm.errors[`shipping.${index}.shipping_notes`]"/>
          </td>
          <td colspan="2"></td>
        </tr>
        </tbody>
      </table>
    </div>
    <p v-if="shippingForm.shipping === 0">No records...</p>
    <div class="overflow-x-auto">
      <table class="min-w-full w-full text-left text-sm font-light mt-2">
        <thead class="border-b font-medium dark:border-neutral-500">
        <tr>
          <th scope="col" class="px-4 py-2">Style</th>
          <th scope="col" class="px-4 py-2">Size</th>
          <th scope="col" class="px-4 py-2">Qty Ordered</th>
          <th scope="col" class="px-4 py-2">Left to ship</th>
          <th scope="col" class="px-4 py-2">Quantity</th>
          <th scope="col" class="px-4 py-2">Is Received?</th>
        </tr>
        </thead>
        <tbody>
        <tr class="border-b transition duration-300 ease-in-out hover:bg-neutral-200"
            v-for="(quantity, quantityIndex) in shippingForm.shipping[index].quantities">
          <td class="px-4 py-2 break-words" v-text="quantity.style.style"></td>
          <td class="px-4 py-2 break-words" v-text="quantity.style.vendor_style"></td>
          <td class="px-4 py-2 break-words" v-text="quantity.style.garments"></td>
          <td class="px-4 py-2 break-words">
            <span v-text="`${quantity.style.garments - (quantity.style.quantity_shipped || 0)}`"></span>
          </td>
          <td class="px-4 py-2 break-words">
            <NumberInput
              type="text"
              class="mt-1 w-1/4"
              v-model="shippingForm.shipping[index].quantities[quantityIndex].qty_ship"
              :disabled="(!helpers.userCanManageProject(currentUser) && !helpers.userIsVendor(currentUser))"
            />
            <InputError :message="shippingForm.errors[`shipping.${index}.quantities.${quantityIndex}.qty_ship`]"/>
          </td>
          <td>
            <label class="flex items-center"
                   :for="`received-${shippingForm.shipping[index].quantities[quantityIndex].qty_id}`">
              <Checkbox v-model:checked="shippingForm.shipping[index].quantities[quantityIndex].is_received"
                        :id="`received-${shippingForm.shipping[index].quantities[quantityIndex].qty_id}`"/>
              <span class="ml-2">Received</span>
            </label>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md"
       v-if="shippingForm.shipping.length === 0">
    <p>No records...</p>
  </div>
  <div
    class="flex items-center justify-center px-4 py-3 bg-gray-50 text-right sm:px-6 shadow sm:rounded-bl-md sm:rounded-br-md sticky bottom-0">
    <PrimaryButton type="button" class="mr-2" :disabled="savingShipping" @click.prevent="addNewShippingRow()"
                   v-if="hideSave === false">Add New Tracking
      Information
    </PrimaryButton>
    <PrimaryButton @click.prevent="saveShippingForm()" :disabled="savingShipping" v-if="hideSave === false">Save
    </PrimaryButton>
  </div>
</template>
